<!--
  Private LINE-theme asset workbench (noindex, not linked). Same shape as the
  Pinterest gallery: a full-width section stacked per theme. Each theme's board
  is painted in its OWN editorBackground and shows every LINE asset, grouped by
  the LINE asset type, rendered live in HTML/CSS/SVG so they can be tuned on the
  site and exported to PNG on each update.

  Modes:
    ?only=<key>          render only that theme's board
    ?asset=<type>&only=  render ONLY that asset at native px for a 1:1 export
                         (currently: menu-bg, passcode, thumbnails [&v=store|ios|android])
-->
<!--
  Theme rail (owner 2026-08-15): the workbench is twenty full-height boards, so
  a fixed sidebar lists every theme and scrolls to its board on click, with the
  board being read highlighted. Not rendered in capture mode or with ?only=,
  where there is a single asset / board and nothing to navigate.
-->
<nav class="board-nav" *ngIf="!captureAsset && !onlyKey">
  <div class="board-nav-title">Themes</div>
  <ng-container *ngFor="let line of navLines">
    <div class="board-nav-group">{{ line.name }}</div>
    <button type="button" class="board-nav-item"
            *ngFor="let product of line.products; trackBy: trackByProductKey"
            [class.is-active]="activeKey === product.key"
            (click)="scrollToTheme(product.key)">
      <span class="board-nav-dot" [style.background]="navDot(product)"></span>
      <span class="board-nav-name">{{ product.name }}</span>
    </button>
  </ng-container>
</nav>

<div class="line-asset-gallery" [class.capture]="captureAsset" [class.has-nav]="!captureAsset && !onlyKey">
  <ng-container *ngFor="let line of themes; trackBy: trackByLineKey">
    <ng-container *ngFor="let product of line.products; trackBy: trackByProductKey">

      <!-- Capture mode: a single asset, native px, nothing else. (LINE has no
           all-in-one bundle, so it is excluded everywhere on this page.) -->
      <app-line-menu-bg *ngIf="captureAsset === 'menu-bg' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                        class="capture-asset menu-bg-asset"
                        [product]="product"></app-line-menu-bg>

      <!-- Chat background capture: the transparent kawaii wallpaper at iOS
           native px (1482×1334) for a 1:1 PNG export. Rendered on NO background
           so the exported PNG keeps its alpha (the color skin is the sky). -->
      <app-line-chat-bg *ngIf="captureAsset === 'chat-bg' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                        class="capture-asset chat-bg-asset"
                        [style.width.px]="1482" [style.height.px]="1334"
                        [product]="product"></app-line-chat-bg>

      <!-- Thumbnail capture: ONE store/iOS/Android main image (?v=) at its
           native px, for a 1:1 PNG export. -->
      <app-line-thumbnail *ngIf="captureAsset === 'thumbnails' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                          class="capture-asset thumbnail-asset"
                          [product]="product"
                          [category]="line.name"
                          [variant]="captureVariant"></app-line-thumbnail>

      <!-- Passcode capture: ONE dot (i_12..i_19 order via ?pc=) at its native px
           (?sz=120 iOS / 116 Android), transparent, for a 1:1 PNG export. -->
      <app-line-passcode *ngIf="captureAsset === 'passcode' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                         class="capture-asset passcode-asset"
                         [style.width.px]="captureSize"
                         [style.height.px]="captureSize"
                         [on]="passcodeDots(product)[capturePcIndex]?.on"
                         [color]="passcodeDots(product)[capturePcIndex]?.color"
                         [seed]="passcodeDots(product)[capturePcIndex]?.seed"
                         [variant]="passcodeVariant(product)"></app-line-passcode>

      <!-- Menu-button capture: ONE tab icon (OFF/ON via ?mb=index) at native 128×150. -->
      <app-line-menu-button *ngIf="captureAsset === 'menu-buttons' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                            class="capture-asset menu-button-asset"
                            [product]="product"
                            [name]="menuButtons[captureMbIndex]?.name"
                            [on]="menuButtons[captureMbIndex]?.on"></app-line-menu-button>

      <!-- iOS 26 menu-button capture: ONE Liquid Glass tab icon (i_XX_g) at native 80×56. -->
      <app-line-menu-button *ngIf="captureAsset === 'menu-buttons-g' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                            class="capture-asset menu-button-g-asset"
                            [product]="product"
                            [g]="true"
                            [name]="menuButtons[captureMbIndex]?.name"
                            [on]="menuButtons[captureMbIndex]?.on"></app-line-menu-button>

      <!-- Profile capture: ONE avatar (person/group via ?pf=) at native px (?sz=240 iOS / 247 Android). -->
      <app-line-profile *ngIf="captureAsset === 'profile' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                        class="capture-asset profile-asset"
                        [style.width.px]="captureSize"
                        [style.height.px]="captureSize"
                        [product]="product"
                        [kind]="captureProfileKind"></app-line-profile>

      <!-- Screenshot capture (?asset=screenshot): BOTH phone previews fanned
           like a card pair (home screen behind-left, chat front-right), pinned
           top-left, so ONE marketplace screenshot shows the two screens. The
           generator trims the transparent margin + resizes to cap. -->
      <div *ngIf="captureAsset === 'screenshot' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
           class="capture-asset screenshot-quad-asset">
        <app-line-home class="quad-ios26"
                       [product]="product" [category]="line.name" [ios26]="true"></app-line-home>
        <app-line-home class="quad-home"
                       [product]="product" [category]="line.name"></app-line-home>
        <app-line-card class="quad-chat"
                       [product]="product" [productLine]="line"></app-line-card>
        <app-line-lock class="quad-lock"
                       [product]="product"></app-line-lock>
      </div>

      <!-- Screenshot 2 (?asset=screenshot2): the second preview, the LINE home screen, pinned
           top-left, for a 1:1 marketplace screenshot (same trim + resize as screenshot 1). -->
      <app-line-home *ngIf="captureAsset === 'screenshot2' && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
                     class="capture-asset screenshot-asset"
                     [product]="product"
                     [category]="line.name"></app-line-home>

      <!-- Normal mode: a full-width board in the theme's own editor background. -->
      <section *ngIf="!captureAsset && product.key !== 'all-in-one' && (!onlyKey || product.key === onlyKey)"
               class="theme-board"
               [attr.data-key]="product.key"
               [style.background]="product.designTokens?.editorBackground || '#0c1620'"
               [style.color]="product.designTokens?.primaryForeground || '#e7eef3'">

        <div class="board-layout">

          <!-- LEFT: logo + theme name. -->
          <div class="board-left">
            <header class="board-head">
              <img class="board-logo" [src]="product.logo" alt="">
              <div class="board-head-text">
                <div class="board-eyebrow"
                     [style.color]="product.designTokens?.secondaryForeground || ''">
                  {{ line.name }}<span *ngIf="product.appearance"> · {{ product.appearance }}</span>
                </div>
                <h2 class="board-name" [style.color]="boardFg(product)">{{ product.name }}</h2>
              </div>
            </header>

            <!-- Under the name: the colour circles, then the inspiration text. -->
            <div class="board-meta">
              <div class="board-colors" *ngIf="product.compactPalette?.length">
                <span class="board-color" *ngFor="let c of product.compactPalette"
                      [style.background]="c"></span>
              </div>
              <p class="board-desc" *ngIf="product.description"
                 [style.color]="product.designTokens?.secondaryForeground || ''">
                {{ product.description }}
              </p>
            </div>

          </div>

          <!-- RIGHT: every image LINE wants, group by group, showing the assets
               we currently have (generator output). We tune each here and tick
               it off (✓). Order mirrors generate-line-theme.py. -->
          <div class="board-right">
            <div class="asset-groups">

              <!-- ONE tick, reused by every row below. `k` is the tick's key: a
                   plain group key for the single-tile rows, `group:index` for
                   each tile in a multi-tile row, so every asset in a row is
                   approved on its own (owner 2026-08-12). Clicking always flips
                   the current state, including OFF again for a tick that
                   `doneAssets` declares done. -->
              <ng-template #tick let-k>
                <span class="asset-tick" (click)="toggleAssetDone(product.key, k)"
                      [class.done]="isAssetDone(product.key, k)"
                      [style.--tick-color]="isAssetDone(product.key, k) ? (product.designTokens?.success || '#46d27e') : (product.designTokens?.error || '#e05555')"></span>
              </ng-template>

              <section class="asset-group"
                       *ngFor="let a of lineAssets"
                       [class.asset-group--wide]="a.wide"
                       [attr.data-asset]="a.key">

                <h3 class="group-label" [style.color]="product.designTokens?.secondaryForeground || ''">
                  <span class="group-title">{{ a.title }}</span>
                  <span class="group-dim">{{ a.sizes }}</span>
                </h3>
                <div class="group-note">{{ a.note }}</div>

                <!-- Each individual asset carries its OWN tick. Not done -> an
                     open ring in the theme's --error; done -> filled --success.
                     (We flip per-asset state as each one is finished.) -->
                <!-- Each asset's tick sits BELOW it (never over it). Not done ->
                     an open ring in --error; done -> filled --success + check. -->
                <div class="group-body" [ngSwitch]="a.key">

                  <!-- Previews: the key screens side by side (owner 2026-07-29),
                       a section like the others. Only our updates (kawaii
                       wallpaper + tree) replace LINE's stock art; the rest
                       mirrors LINE's own preview, theme-coloured. -->
                  <div *ngSwitchCase="'previews'" class="public-preview">
                    <app-line-home [product]="product" [category]="line.name" [ios26]="true"></app-line-home>
                    <app-line-home [product]="product" [category]="line.name"></app-line-home>
                    <app-line-card [product]="product" [productLine]="line"></app-line-card>
                    <app-line-lock [product]="product"></app-line-lock>
                  </div>

                  <!-- Main images: the three store thumbnails drawn LIVE
                       (wallpaper + logo + name + category + appearance + the
                       theme-colour rule, no screenshots). -->
                  <div *ngSwitchCase="'thumbnails'" class="asset-imgs">
                    <div class="asset-item" *ngFor="let v of thumbnailVariants; let i = index">
                      <app-line-thumbnail class="thumb-tile" [product]="product"
                                          [category]="line.name" [variant]="v"></app-line-thumbnail>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>

                  <!-- Menu background: the live SVG redesign. -->
                  <div *ngSwitchCase="'menu-bg'" class="asset-item asset-item--wide">
                    <div class="menu-bg-frame">
                      <app-line-menu-bg [product]="product"></app-line-menu-bg>
                    </div>
                    <ng-container *ngTemplateOutlet="tick; context: { $implicit: a.key }"></ng-container>
                  </div>

                  <!-- Menu buttons: the 18 tab glyphs drawn LIVE (Phosphor
                       masks tinted by CSS), OFF muted + ON electric, matching
                       the menu-background ribbons. -->
                  <div *ngSwitchCase="'menu-buttons'" class="asset-imgs">
                    <div class="asset-item" *ngFor="let b of menuButtonsDisplay; let i = index">
                      <app-line-menu-button class="mb-tile" [product]="product"
                                            [name]="b.name" [on]="b.on"></app-line-menu-button>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>

                  <!-- iOS 26 Liquid Glass buttons (80×56): every icon as a
                       dark|light PAIR box (the same glyph on black and on
                       white, side by side) with one tick per pair — both of
                       LINE's required readabilities reviewed per icon. -->
                  <div *ngSwitchCase="'menu-buttons-g'" class="asset-imgs">
                    <div class="asset-item g-pair-item" *ngFor="let b of menuButtonsDisplay; let i = index">
                      <!-- LIGHT tile first, then DARK (owner 2026-07-29). -->
                      <div class="g-pair">
                        <app-line-menu-button class="mb-tile g-light-tile" [product]="product"
                                              [g]="true" [name]="b.name" [on]="b.on"></app-line-menu-button>
                        <app-line-menu-button class="mb-tile g-dark-tile" [product]="product"
                                              [g]="true" [name]="b.name" [on]="b.on"></app-line-menu-button>
                      </div>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>

                  <!-- Profile avatars drawn LIVE: person (green->blue) then
                       group (blue->pink), one left-to-right electric sweep. -->
                  <div *ngSwitchCase="'profile'" class="asset-imgs">
                    <div class="asset-item" *ngFor="let k of [ 'person', 'group' ]; let i = index">
                      <app-line-profile class="lp-asset" [product]="product" [kind]="$any(k)"></app-line-profile>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>

                  <!-- Passcode: live SVG dots, a tick under EACH (like every
                       other asset). OFF = hollow ring in the digit's colour,
                       ON = filled + glow. -->
                  <div *ngSwitchCase="'passcode'" class="asset-imgs">
                    <div class="asset-item" *ngFor="let d of passcodeDots(product); let i = index">
                      <!-- Passcode dots on their real passcode-screen background
                           (the pink upper zone) so the white OFF dots are visible
                           in the asset section too (owner 2026-07-29). -->
                      <div class="pc-back" [style.background]="passcodeBg(product)">
                        <app-line-passcode class="pc-tile" [on]="d.on" [color]="d.color" [seed]="d.seed"
                                           [variant]="passcodeVariant(product)"></app-line-passcode>
                      </div>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>

                  <!-- Chat background: the transparent kawaii wallpaper, shown
                       composited over its flat color-skin sky (skinColor) so the
                       board previews exactly what a phone renders. The exported
                       PNG itself stays transparent (the sky is the color skin). -->
                  <div *ngSwitchCase="'chat-bg'" class="asset-imgs">
                    <div class="asset-item">
                      <div class="chatbg-thumb" [style.background]="chatSky(product)">
                        <app-line-chat-bg [product]="product"></app-line-chat-bg>
                      </div>
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: a.key }"></ng-container>
                    </div>
                  </div>

                  <!-- Everything else (currently only the thumbnails, still to
                       be drawn natively): the generated PNG baseline if it has
                       been copied locally; gitignored, so it may be absent. -->
                  <div *ngSwitchDefault class="asset-imgs">
                    <div class="asset-item" *ngFor="let f of a.files; let i = index">
                      <img [src]="assetSrc(product.key, f)" [alt]="a.title + ' ' + f" loading="lazy">
                      <ng-container *ngTemplateOutlet="tick; context: { $implicit: assetItemKey(a.key, i) }"></ng-container>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

        </div>
      </section>

    </ng-container>
  </ng-container>
</div>
